'use client';
import React, { useState } from 'react';
import { X, Phone, Lock, Eye, EyeOff, CheckCircle, ArrowRight, UserPlus, LogIn, Shield } from 'lucide-react';

interface SellerAuthGateProps {
  onClose: () => void;
  onAuthenticated: () => void;
  action?: string; // e.g. "post a listing", "apply for a job"
}

type GateStep = 'choice' | 'signin_phone' | 'signin_pin' | 'signup_phone' | 'signup_otp' | 'signup_name' | 'signup_pin' | 'success';

export default function SellerAuthGate({ onClose, onAuthenticated, action = 'post' }: SellerAuthGateProps) {
  const [step, setStep] = useState<GateStep>('choice');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpTimer, setOtpTimer] = useState(60);
  const [timerActive, setTimerActive] = useState(false);

  const startTimer = () => {
    setTimerActive(true);
    setOtpTimer(60);
    const interval = setInterval(() => {
      setOtpTimer(prev => {
        if (prev <= 1) { clearInterval(interval); setTimerActive(false); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSignIn = () => {
    if (!phone.trim()) { setError('Enter your phone number'); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setStep('signin_pin'); setError(''); }, 800);
  };

  const handleSignInPin = () => {
    if (pin.length < 4) { setError('Enter your PIN'); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setStep('success'); setError(''); }, 1000);
  };

  const handleSignUpPhone = () => {
    if (!phone.trim()) { setError('Enter your phone number'); return; }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep('signup_otp');
      startTimer();
      setError('');
    }, 800);
  };

  const handleVerifyOtp = () => {
    if (otp.length < 6) { setError('Enter the 6-digit OTP'); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setStep('signup_name'); setError(''); }, 800);
  };

  const handleName = () => {
    if (!name.trim()) { setError('Enter your full name'); return; }
    setStep('signup_pin');
    setError('');
  };

  const handleCreatePin = () => {
    if (pin.length < 4) { setError('PIN must be at least 4 digits'); return; }
    if (pin !== confirmPin) { setError('PINs do not match'); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setStep('success'); setError(''); }, 1000);
  };

  const handleSuccess = () => { onAuthenticated(); onClose(); };

  const inputClass = "w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all";
  const inputStyle = (hasError: boolean) => ({
    background: 'var(--input)',
    border: `1.5px solid ${hasError ? 'var(--danger)' : 'var(--border)'}`,
    color: 'var(--foreground)'
  });

  return (
    <div className="fixed inset-0 z-[200] flex items-end lg:items-center justify-center">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }} onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-t-3xl lg:rounded-3xl overflow-hidden"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,200,83,0.12)' }}>
              <Shield size={18} style={{ color: 'var(--primary)' }} />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                {step === 'choice' ? 'Sign In Required' : step === 'success' ? 'Welcome!' : step.startsWith('signup') ? 'Create Account' : 'Sign In'}
              </h2>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                {step === 'choice' ? `You need an account to ${action}` : 'MKUBWA Platform'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
            <X size={16} style={{ color: 'var(--muted-foreground)' }} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">

          {/* CHOICE */}
          {step === 'choice' && (
            <>
              <p className="text-sm text-center" style={{ color: 'var(--muted-foreground)' }}>
                To <strong style={{ color: 'var(--foreground)' }}>{action}</strong>, you need a verified MKUBWA account.
              </p>
              <button onClick={() => { setStep('signin_phone'); setError(''); }}
                className="w-full flex items-center gap-3 px-4 py-4 rounded-2xl font-semibold text-sm transition-all"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                <LogIn size={18} />
                <span>Sign In to My Account</span>
                <ArrowRight size={16} className="ml-auto" />
              </button>
              <button onClick={() => { setStep('signup_phone'); setError(''); }}
                className="w-full flex items-center gap-3 px-4 py-4 rounded-2xl font-semibold text-sm transition-all"
                style={{ background: 'var(--secondary)', border: '1.5px solid var(--border)', color: 'var(--foreground)' }}>
                <UserPlus size={18} style={{ color: 'var(--primary)' }} />
                <span>Create New Account</span>
                <ArrowRight size={16} className="ml-auto" style={{ color: 'var(--muted-foreground)' }} />
              </button>
              <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
                Free to join · Phone number only · No social login required
              </p>
            </>
          )}

          {/* SIGN IN — PHONE */}
          {step === 'signin_phone' && (
            <>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Phone Number</label>
                <input className={inputClass} style={inputStyle(!!error)} type="tel" placeholder="+254 7XX XXX XXX"
                  value={phone} onChange={e => { setPhone(e.target.value); setError(''); }} />
                {error && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>}
              </div>
              <button onClick={handleSignIn} disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm disabled:opacity-60"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><ArrowRight size={16} /> Continue</>}
              </button>
              <button onClick={() => setStep('choice')} className="text-xs text-center w-full" style={{ color: 'var(--muted-foreground)' }}>← Back</button>
            </>
          )}

          {/* SIGN IN — PIN */}
          {step === 'signin_pin' && (
            <>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Enter your PIN for <strong style={{ color: 'var(--foreground)' }}>{phone}</strong></p>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Secret PIN</label>
                <div className="relative">
                  <input className={inputClass} style={inputStyle(!!error)} type={showPin ? 'text' : 'password'}
                    placeholder="Enter PIN" inputMode="numeric" maxLength={6}
                    value={pin} onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }} />
                  <button onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPin ? <EyeOff size={16} style={{ color: 'var(--muted-foreground)' }} /> : <Eye size={16} style={{ color: 'var(--muted-foreground)' }} />}
                  </button>
                </div>
                {error && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>}
              </div>
              <button onClick={handleSignInPin} disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm disabled:opacity-60"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Lock size={16} /> Sign In</>}
              </button>
              <button onClick={() => setStep('signin_phone')} className="text-xs text-center w-full" style={{ color: 'var(--muted-foreground)' }}>← Back</button>
            </>
          )}

          {/* SIGN UP — PHONE */}
          {step === 'signup_phone' && (
            <>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Mobile Phone Number</label>
                <input className={inputClass} style={inputStyle(!!error)} type="tel" placeholder="+254 7XX XXX XXX"
                  value={phone} onChange={e => { setPhone(e.target.value); setError(''); }} />
                {error && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>}
              </div>
              <button onClick={handleSignUpPhone} disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm disabled:opacity-60"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Phone size={16} /> Send OTP</>}
              </button>
              <button onClick={() => setStep('choice')} className="text-xs text-center w-full" style={{ color: 'var(--muted-foreground)' }}>← Back</button>
            </>
          )}

          {/* SIGN UP — OTP */}
          {step === 'signup_otp' && (
            <>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>OTP sent to <strong style={{ color: 'var(--foreground)' }}>{phone}</strong></p>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>6-Digit OTP</label>
                <input className={inputClass} style={inputStyle(!!error)} type="tel" placeholder="Enter OTP"
                  inputMode="numeric" maxLength={6}
                  value={otp} onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }} />
                {error && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {timerActive ? `Resend in ${otpTimer}s` : 'Didn\'t receive OTP?'}
                </span>
                {!timerActive && (
                  <button onClick={() => { startTimer(); }} className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>Resend OTP</button>
                )}
              </div>
              <button onClick={handleVerifyOtp} disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm disabled:opacity-60"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><CheckCircle size={16} /> Verify OTP</>}
              </button>
            </>
          )}

          {/* SIGN UP — NAME */}
          {step === 'signup_name' && (
            <>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Full Name</label>
                <input className={inputClass} style={inputStyle(!!error)} type="text" placeholder="Your full name"
                  value={name} onChange={e => { setName(e.target.value); setError(''); }} />
                {error && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>}
              </div>
              <button onClick={handleName}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                <ArrowRight size={16} /> Continue
              </button>
            </>
          )}

          {/* SIGN UP — PIN */}
          {step === 'signup_pin' && (
            <>
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Create Secret PIN</label>
                  <div className="relative">
                    <input className={inputClass} style={inputStyle(!!error)} type={showPin ? 'text' : 'password'}
                      placeholder="4–6 digit PIN" inputMode="numeric" maxLength={6}
                      value={pin} onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }} />
                    <button onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2">
                      {showPin ? <EyeOff size={16} style={{ color: 'var(--muted-foreground)' }} /> : <Eye size={16} style={{ color: 'var(--muted-foreground)' }} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Confirm PIN</label>
                  <input className={inputClass} style={inputStyle(!!error)} type={showPin ? 'text' : 'password'}
                    placeholder="Confirm PIN" inputMode="numeric" maxLength={6}
                    value={confirmPin} onChange={e => { setConfirmPin(e.target.value.replace(/\D/g, '')); setError(''); }} />
                  {error && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>}
                </div>
              </div>
              <button onClick={handleCreatePin} disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm disabled:opacity-60"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><CheckCircle size={16} /> Create Account</>}
              </button>
            </>
          )}

          {/* SUCCESS */}
          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,200,83,0.12)' }}>
                <CheckCircle size={32} style={{ color: 'var(--primary)' }} />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>You're in!</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>Account verified. You can now {action}.</p>
              </div>
              <button onClick={handleSuccess}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-semibold text-sm"
                style={{ background: 'var(--primary)', color: '#fff' }}>
                <ArrowRight size={16} /> Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
